import { z } from 'zod';

export const exerciseLoadType = z.enum(['weighted', 'bodyweight', 'timed']);

export const plannedExerciseOutput = z.object({
  workoutExerciseId: z.string(),
  exerciseId: z.string(),
  name: z.string(),
  /**
   * Odreduje koja polja ekran treninga uopce prikazuje. Sklek nema kilazu,
   * plank nema ponavljanja — bez ovoga je svaka vjezba dobivala oba polja.
   */
  loadType: exerciseLoadType,
  sortOrder: z.number().int(),
  sets: z.number().int().nullable(),
  reps: z.string().nullable(),
  weightHintKg: z.number().nullable(),
  restSeconds: z.number().int().nullable(),
  tempo: z.string().nullable(),
  trainerNote: z.string().nullable(),
  videoKey: z.string().nullable(),
  /**
   * Potpisani URL sličice vježbe, ne ključ. Ključ sam po sebi klijentu ne
   * znači ništa — R2 se čita isključivo kroz `/v1/files` s potpisom.
   */
  imageUrl: z.string().nullable(),
  prescribedSets: z.array(
    z.object({
      setIndex: z.number().int(),
      reps: z.string().nullable(),
      weightKg: z.number().nullable(),
      restSeconds: z.number().int().nullable(),
    }),
  ),
  /** Zadnji put kad je klijent radio ovu vježbu — za "prošli put 60 kg × 10". */
  lastPerformance: z
    .object({ weightKg: z.number().nullable(), reps: z.number().nullable(), on: z.string() })
    .nullable(),
});

export const plannedWorkoutOutput = z.object({
  workoutId: z.string(),
  assignmentId: z.string(),
  name: z.string(),
  type: z.enum(['strength', 'cardio', 'mobility', 'rest', 'mixed']),
  estimatedMinutes: z.number().int().nullable(),
  note: z.string().nullable(),
  scheduledOn: z.string(),
  weekIndex: z.number().int(),
  dayIndex: z.number().int(),
  completedAt: z.string().nullable(),
  exercises: z.array(plannedExerciseOutput),
});

/** Zaključan trening iza probnog praga — legitiman odgovor, ne greška. */
export const workoutLockOutput = z.object({
  /** Redni broj treninga u programu, od nule. */
  ordinal: z.number().int(),
  /** Koliko ih je besplatno prije plaćanja. */
  trialCount: z.number().int(),
  productId: z.string().nullable(),
});

export const todayOutput = z.object({
  day: z.string(),
  /** null = danas nema propisanog treninga (dan odmora, nema programa, zaključano). */
  workout: plannedWorkoutOutput.nullable(),
  /**
   * `locked` je ono što `apps/api` stvarno vraća otkad postoji probni pristup.
   * `no_access` ostaje u popisu jer ga mobilna aplikacija još grana.
   */
  reason: z.enum(['ok', 'rest_day', 'no_program', 'no_access', 'locked']),
  /** Prisutno samo uz `reason: 'ok'`. */
  access: z.enum(['trial', 'full']).optional(),
  /**
   * Prisutno samo uz `reason: 'locked'`. `nullish`, ne `optional`: mobilna
   * aplikacija polje već drži kao `lock?: WorkoutLock | null`.
   */
  lock: workoutLockOutput.nullish(),
});

/** `GET /v1/training/workouts/:id` — isti trening kao u `today`, plus razina pristupa. */
export const plannedWorkoutDetailOutput = plannedWorkoutOutput.extend({
  access: z.enum(['trial', 'full']),
});

export const weekPlanOutput = z.object({
  weekIndex: z.number().int(),
  label: z.string().nullable(),
  /**
   * Naziv dodijeljenog programa i ukupan broj tjedana. Bez njih klijent na
   * ekranu plana ne vidi ni što trenira ni koliko je daleko dogurao — portal je
   * ta dva podatka dosad izmišljao u mocku.
   */
  programName: z.string().nullable(),
  weeksTotal: z.number().int(),
  /**
   * Ponedjeljak i nedjelja tog tjedna programa.
   *
   * Ne da se pouzdano izvesti iz `days`: dan bez propisanog treninga nema
   * datum, pa tjedan koji počinje odmorom na ekranu nije imao početak. Bez
   * raspona je „Tjedan 1" bila oznaka bez veze s kalendarom — klijent nije
   * mogao znati gleda li tjedan koji tek dolazi ili onaj od prije mjesec dana.
   * `null` samo kad klijent nema dodijeljen program.
   */
  weekStart: z.string().nullable(),
  weekEnd: z.string().nullable(),
  days: z.array(
    z.object({
      dayIndex: z.number().int(),
      scheduledOn: z.string(),
      workoutId: z.string().nullable(),
      name: z.string().nullable(),
      type: z.string().nullable(),
      completed: z.boolean(),
      missed: z.boolean(),
    }),
  ),
});

export const dashboardOutput = z.object({
  day: z.string(),
  firstName: z.string().nullable(),
  hasActiveAccess: z.boolean(),
  todayWorkout: z
    .object({ workoutId: z.string(), name: z.string(), exerciseCount: z.number().int(), estimatedMinutes: z.number().int().nullable() })
    .nullable(),
  week: z.object({ completed: z.number().int(), prescribed: z.number().int() }),
  nutrition: z.object({ kcal: z.number(), targetKcal: z.number().nullable(), waterMl: z.number() }),
  weight: z.object({ current: z.number().nullable(), goal: z.number().nullable(), startedAt: z.number().nullable() }),
  streakDays: z.number().int(),
  unreadMessages: z.number().int(),
  lastTrainerMessage: z.object({ body: z.string(), at: z.string() }).nullable(),
});

export type ExerciseLoadType = z.infer<typeof exerciseLoadType>;
export type TodayOutput = z.infer<typeof todayOutput>;
export type WorkoutLockOutput = z.infer<typeof workoutLockOutput>;
export type PlannedWorkoutOutput = z.infer<typeof plannedWorkoutOutput>;
export type PlannedWorkoutDetailOutput = z.infer<typeof plannedWorkoutDetailOutput>;
export type WeekPlanOutput = z.infer<typeof weekPlanOutput>;
export type DashboardOutput = z.infer<typeof dashboardOutput>;
